import {
  faCheck,
  faPen,
  faPlus,
  faSearch,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import Pagination from 'components/pagination';
import useApi from 'helpers/api';
import { useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Form,
  FormControl,
  Row
} from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import ProductServiceService from 'services/ProductService.service';
import CatalogItemDeleteModal from './CatalogItemDeleteModal';
import CatalogItemModal from './CatalogItemModal';

const CatalogItems = () => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const [items, setItems] = useState({
    content: [],
    totalElements: 0
  });

  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    searchTerm: ''
  });

  const [searchTerm, setSearchTerm] = useState('');

  const getData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await ProductServiceService.list(queryParams);
      if (response.content) {
        setItems(response);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos/serviços:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [filters]);

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900 w-50' }
      },
      cell: rowData => {
        const { name } = rowData.row.original;
        return <div>{name}</div>;
      }
    },
    {
      accessorKey: 'type',
      header: 'Tipo',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { type } = rowData.row.original;
        return (
          <div>
            {type === 'SERVICE' ? (
              <Badge bg="white" className="text-dark fs-11">
                Serviço
              </Badge>
            ) : (
              <Badge bg="white" className="text-dark fs-11">
                Produto
              </Badge>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'unitPrice',
      header: 'Preço Unitário',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { unitPrice } = rowData.row.original;
        return <div>{formatCurrency(unitPrice)}</div>;
      }
    },
    {
      accessorKey: 'inactive',
      header: 'Ativo',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return (
          <FontAwesomeIcon
            icon={inactive ? faTimes : faCheck}
            className={inactive ? 'text-danger' : 'text-success'}
          />
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <ButtonGroup>
            <Button variant="link" size="sm" onClick={() => handleEdit(item)}>
              <FontAwesomeIcon icon={faPen} className="text-primary" />
            </Button>
            <Button
              variant="link"
              className="text-danger"
              size="sm"
              onClick={() => handleDelete(item)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </ButtonGroup>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: items.content,
    columns
  });

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleOpenModal = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = async item => {
    try {
      const response = await ProductServiceService.findById(item.id);
      if (response.id) {
        setEditingItem(response);
        setShowModal(true);
      } else {
        showErrorNotification('Erro ao carregar produto/serviço');
      }
    } catch (error) {
      showErrorNotification('Erro ao carregar produto/serviço');
    }
  };

  const handleDelete = item => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedItem(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    try {
      const response = await ProductServiceService.delete(selectedItem.id);
      if (response.status === 204 || !response.userMessage) {
        showSuccessNotification('Produto/Serviço excluído com sucesso');
        getData();
        handleCloseDeleteModal();
      } else {
        showErrorNotification(response.userMessage || 'Erro ao excluir');
      }
    } catch (error) {
      showErrorNotification('Erro ao excluir produto/serviço');
    }
  };

  const handleModalSuccess = () => {
    getData();
    handleCloseModal();
  };

  const debouncedSearch = useDebounce(searchValue => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      searchTerm: searchValue
    }));
  }, 400);

  // Efeito para executar a busca com debounce quando o valor do filtro mudar
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm]);

  const isEmpty = !isLoading && items.content.length === 0;

  return (
    <>
      <CatalogItemModal
        showModal={showModal}
        handleClose={handleCloseModal}
        editingItem={editingItem}
        onSuccess={handleModalSuccess}
      />

      <CatalogItemDeleteModal
        showModal={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        item={selectedItem}
      />

      <FalconComponentCard>
        <FalconComponentCard.Header title="Produtos e Serviços">
          <Flex>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleOpenModal}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
              Novo
            </Button>
          </Flex>
        </FalconComponentCard.Header>

        <FalconComponentCard.Body className="bg-white">
          <Row className="justify-content-between g-0">
            <Col className="d-flex align-items-center col-auto pe-3 mb-3">
              <div className="position-relative input-search-width">
                <FormControl
                  size="sm"
                  id="search"
                  type="search"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="rounded-pill ps-4"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-secondary"
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px'
                  }}
                />
              </div>
            </Col>

            <Col className="col-auto px-3">
              <Form.Select
                size="sm"
                className="mb-3 rounded-pill"
                onChange={e =>
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    includeInactives: e.target.value
                  }))
                }
              >
                <option value="true">Filtro por status</option>
                <option value="true">Todos</option>
                <option value="false">Somente ativos</option>
              </Form.Select>
            </Col>
          </Row>
          {isLoading ? (
            <Skeleton count={10} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <EmptyList imgSize={500} vhSize={75} />
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={items}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default CatalogItems;
